// Pilot help guides (Jill 2026-11-04): the same step-by-step form as the
// training guides in helpGuides.js, one guide per part of a pilot's work, in
// the order a pilot really works: the schedule, starting the trip and the
// brief, the slip aboard, the expense report and Filed after, then the pages
// a pilot opens any time, and the portal on a phone.
//
// Pictures are JPEGs in public/help/<guide>/, taken in the demo sandbox at
// iPad mini size with made-up pilots and a made-up seventh ship. Every
// "you get an email", "it counts", "it goes to" and who-sees-it claim was
// checked against the code on 2026-11-04 (notes in the PR).
//
// Added 2026-11-05, after the portal came out of beta (Jill 2026-11-04):
// Preview and submit for the slip and the expense report, Submit with no
// signal, correcting a filed slip or report, and pictures for a shift and a
// Laying to / DP event. Each was checked against the code that day.

export const PILOT_GUIDES = [
  {
    key: "pilot-schedule",
    title: "Pilot guide: your schedule and travel",
    about: "The Dispatch tab: your assignments, what changed, your travel, the other pilots, and the winter roster.",
    topics: [
      {
        key: "schedule",
        title: "Read My Schedule",
        intro: "Dispatch opens on My Schedule, built from the office dispatch sheet and the CLAA calendar. The last copy is kept on your device, so it opens with no signal.",
        steps: [
          { img: "schedule-01.jpg", text: "The Next Assignment card is your next ship: the dates, the other pilot on the sheet, the first port, and any trainee riding. Open Trip Workspace takes you straight to that trip's slip, brief and expense report." },
          { img: "schedule-02.jpg", text: "The three week grid shows each day: a ship and its port, a travel or hotel day, or a day off. Winter weeks read on dispatch. The arrows move a week at a time." },
          { img: "schedule-03.jpg", text: "Below the grid, each of your trips is a card with its ports by day. Open Trip Workspace for this ship starts or reopens that trip." },
        ],
      },
      {
        key: "changed",
        title: "What changed for you",
        intro: "After the office publishes a new sheet, the Dispatch tab gets a red dot and My Schedule lists exactly which of your days changed.",
        steps: [
          { img: "changed-01.jpg", text: "What's changed for you lists each day that moved at the last publish: what the sheet said before in red, what it says now in green. You are not comparing two PDFs." },
        ],
      },
      {
        key: "calendar",
        title: "Put your days on your phone calendar",
        steps: [
          { img: "calendar-01.jpg", text: "At the bottom of My Schedule, set the dates, tick Ship days and Travel, and tap Add to Calendar (.ics). Each ship day becomes one all-day event, like COR KTN, with the other pilot's name on the first day of the trip. Tick Only days that changed at the last publish to add just the changes." },
        ],
      },
      {
        key: "travel",
        title: "Your travel: Who's Traveling and the Travel Sheet",
        intro: "Both come from the reservations sheet the office publishes. Your own flights, hotel and pilot boat also show on My Schedule.",
        steps: [
          { img: "travel-01.jpg", text: "Tap Pilots, then Travel, and pick a day. Everyone's travel that day is grouped by where they leave from, with flight, time, confirmation number, ship and hotel. The box at the top says who is at each hotel that night." },
          { img: "travel-02.jpg", text: "Travel Sheet is the office sheet itself, day by day, with confirmation numbers. Pick the dates or tap This week, Next 2 weeks or Next 30 days." },
          { img: "travel-03.jpg", text: "A red struck-through row was cancelled by the office. A blue row changed on the latest upload. To fix a booking, contact the office; the sheet is read-only." },
        ],
      },
      {
        key: "add-travel",
        title: "Add travel by hand",
        intro: "For last minute travel the office has not put on the reservations sheet yet.",
        steps: [
          { img: "addtravel-01.jpg", text: "On My Schedule, tap Add travel." },
          { img: "addtravel-02.jpg", text: "Pick the date and fill in what you know: flight, from and to, hotel, pilot boat, costs. Tap Save. It shows on your schedule like office travel. If the office later publishes the same trip, their version takes over on its own." },
        ],
      },
      {
        key: "full",
        title: "Full Dispatch, the Vessel Calendar, and the sheet codes",
        steps: [
          { img: "full-01.jpg", text: "Full Dispatch is the office layout: pilots down the side, dates across, your own row shaded." },
          { img: "vessels-01.jpg", text: "Vessel Calendar is the same sheet by ship, with each day's port, the CLAA arrival and departure times, and the pilots aboard. Hide arrival and departure times makes it shorter." },
          { img: "codes-01.jpg", text: "Not sure what a cell on the sheet means? Tap What do the sheet codes mean?" },
          { img: "codes-02.jpg", text: "The list explains ship and port cells, the lowercase travel letters, cruise days, X days, blank days and FM. Tap Hide the sheet codes to close it." },
        ],
      },
      {
        key: "pilots",
        title: "Who's Where and Where's Waldo",
        steps: [
          { img: "where-01.jpg", text: "Tap Pilots, then Who's Where, and pick a day. Everyone is grouped by port, with the pilots on dispatch that week at the top." },
          { img: "waldo-01.jpg", text: "Where's Waldo finds the days you share a port with up to three other pilots. Pick them and tap Find." },
          { img: "waldo-02.jpg", text: "Every shared day is listed with where you both are and on which ship. Days you are both off count as your home ports, and a shared charter or ferry leg counts too." },
        ],
      },
      {
        key: "winter",
        title: "Winter Roster: your weeks on dispatch",
        steps: [
          { img: "winter-01.jpg", text: "Winter Roster is the season's roster, one column per week, an X for each week a pilot is on dispatch. Your own row is marked (you), and this week's column is underlined. Your winter weeks also show on My Schedule as On dispatch." },
        ],
      },
    ],
  },
  {
    key: "pilot-brief",
    title: "Pilot guide: starting a trip and the Transit Brief",
    about: "Generate the trip from your assignment, then read the brief: legs, the other ships, chokepoint times, the watch, tides, notices, fisheries and barges.",
    topics: [
      {
        key: "start",
        title: "Start the trip from your Next Assignment",
        intro: "One trip holds the pilot slip, the Transit Brief and the expense report as tabs. Start it from your assignment so the portal fills in what it already knows.",
        steps: [
          { img: "start-01.jpg", text: "On My Schedule, tap Open Trip Workspace on the Next Assignment card." },
          { img: "start-02.jpg", text: "The vessel and the trip start date are filled from your assignment. Tap Generate Trip." },
          { img: "start-03.jpg", text: "The trip opens on the Pilot Slip, with the ports, times, pilot stations and the other pilot from the dispatch sheet already on it. Miles and base times come from the office table. Your own times stay blank for you. Tap Transit Brief for the brief." },
        ],
      },
      {
        key: "legs",
        title: "Read the brief",
        intro: "The brief is built from the slip and the CLAA calendar: one card per leg, in order.",
        steps: [
          { img: "brief-01.jpg", text: "The ship, voyage number, tonnage and IMO are at the top. Export PDF makes a copy you can read with no signal. Switch to classic brief shows the whole voyage on one page." },
          { img: "brief-02.jpg", text: "Tap a leg to open its card: departure and arrival times, distance and the speed needed, the other ships in your waterway that night, the watch, the arrival port with its berth, tides and currents, the other ships in port that day, and barges." },
        ],
      },
      {
        key: "conflicts",
        title: "The other ships in your waterway",
        steps: [
          { img: "conflicts-01.jpg", text: "CONFLICTS counts the ships that share your waterway on that leg. No other ship in your waterway tonight means the night is clear." },
          { img: "conflicts-02.jpg", text: "Each other ship shows her leg, departure and arrival, and when she reaches the chokepoint compared with you. Watch keeps the line marked as one you are watching. Dismiss hides it when you know she went another way." },
        ],
      },
      {
        key: "eta",
        title: "Report your own time at a chokepoint",
        intro: "The brief estimates when you reach each chokepoint on the leg, such as Snow Pass, Cape Decision or North Inian Pass, from the distance table and your scheduled speed. The other pilots see the estimate until you report a real time.",
        steps: [
          { img: "eta-01.jpg", text: "Each chokepoint on the leg shows Your ETA with the estimate, and the current there at that time with its next slack. All times opens the whole day." },
          { img: "eta-02.jpg", text: "Type your real time as four digits and tap Report." },
          { img: "eta-03.jpg", text: "The line now reads reported, with the time you sent it, and the current is worked out for your reported time. The pilots on the other ships see your time instead of the estimate. Back to the estimate clears it." },
        ],
      },
      {
        key: "watch",
        title: "Post the watch for a two pilot night leg",
        steps: [
          { img: "watch-01.jpg", text: "Under Watch, tap Set it up." },
          { img: "watch-02.jpg", text: "Pick who stands first, when the watch starts and where it runs until, then tap One pilot, Halfway, Custom or 3 watches. Custom takes the longest watch in hours and who gets the longer short watch. The moment you tap one, the schedule is posted to the other ships. Done closes the panel. The Watches calculator lower on the brief is a separate tool with its own choices, Watch length and Split it yourself, for working a split out by hand." },
        ],
      },
      {
        key: "tides",
        title: "Tides, currents and the berth",
        steps: [
          { img: "tides-01.jpg", text: "The arrival card names the berth with its heading, length and least depth, says whether the tide is flooding or ebbing at your arrival and departure, and lists the day's highs and lows and the current stations near the port. Tides and currents are NOAA predictions and can differ from the conditions on the day." },
        ],
      },
      {
        key: "notices",
        title: "Notices, fishery openings, whale waters and barges",
        steps: [
          { img: "notices-01.jpg", text: "Notices lists the Coast Guard Local Notice to Mariners items matched to your route, with the date each was first reported. Always check the full list for the waterways you transit." },
          { img: "fisheries-01.jpg", text: "Fisheries lists the ADF&G commercial fishing announcements for the districts on your route." },
          { img: "barge-01.jpg", text: "On a port day, the Lynden barge schedule shows which tug and barge are at that port and when, with the date of the schedule it came from. A Glacier Bay route also shows the Park's whale waters notice." },
        ],
      },
      {
        key: "vessel-info",
        title: "The ship's bridge team and notes, from the brief",
        steps: [
          { img: "vessel-info-01.jpg", text: "Vessel Info on the brief is the same bridge team list and ship notes you find under More, Vessel Info. Edit them here while you are aboard; every pilot sees the same list." },
        ],
      },
      {
        key: "notes",
        title: "Notes that follow you, and the PDF",
        steps: [
          { img: "notes-01.jpg", text: "Each day card has a Notes box. Notes save on their own and sync to your account, so they are on your other device too." },
          { img: "export-01.jpg", text: "Export PDF writes the whole brief to a file you can keep and read with no signal." },
        ],
      },
    ],
  },
  {
    key: "pilot-slip",
    title: "Pilot guide: the pilot slip",
    about: "Your block, the segments as the trip runs, miles from the office table, events, your signature, drafts, the handoff to the other pilot, and the PDF.",
    topics: [
      {
        key: "block",
        title: "Your pilot block",
        intro: "Each pilot on the slip has a block. Fill in your own; the other pilot fills in theirs, or sends you theirs with the handoff.",
        steps: [
          { img: "block-01.jpg", text: "Pick your name at the top of your block. The list takes a moment to load the first time." },
          { img: "block-02.jpg", text: "Dispatch Started, Place On Ship, Place Off Ship and Dispatch Ended each take a place, a date and a time as four digits. Start typing the place and pick it from the office list. The times have to run in that order; the portal stops a slip whose block is out of order. Submitted by marks the pilot who files the slip and prints on the PDF." },
        ],
      },
      {
        key: "segments",
        title: "The voyage segments",
        steps: [
          { img: "segments-01.jpg", text: "Each row is a place in order: pilot station, port, chokepoint or scenic area. The ports came in with their CLAA times. Fill in each place's date and time in and out as the trip runs. Leave an event's Date Out blank and it takes the Date In." },
        ],
      },
      {
        key: "miles",
        title: "Update Miles",
        intro: "Miles come from the office mileage table, never from a guess.",
        steps: [
          { img: "miles-01.jpg", text: "Tap Update Miles and every leg the office table knows is filled and locked. A leg the table does not have gets a red prompt; miles you type there print on the PDF marked hand entered and go to the office file too, with no base time. Anything beyond the table goes in Extra mi on the arrival row of that leg." },
        ],
      },
      {
        key: "events",
        title: "Add Segment and Event for an itinerary that changed",
        steps: [
          { img: "add-01.jpg", text: "+ Add Segment puts a new place row where you want it. + Event records something that happened: an anchoring outside a port, laying to or DP, staying aboard in port, a shift, a sailing delay, a medical evacuation. Something else prints your own words on the slip. Delete a segment… removes a row." },
          { img: "event-01.jpg", text: "Pick what happened, type where, and give the start and end date and time. The row goes in where you say. Event rows carry no miles; the office works out the charge from your times." },
        ],
      },
      {
        key: "pump",
        title: "Out to pump",
        intro: "A ship that goes out past a headland to pump and comes back in at the same headland takes two lines with that headland's name, never a shift.",
        steps: [
          { text: "Add the headland as a segment twice. The first line gets only the OUT time, with a note such as Out to pump. The second line gets only the IN time." },
          { text: "Extra miles never go on the second line: outside the headland you are off duty. Extra miles run on the way out go on the first line. Extra miles run after coming back in go on the next line that ends that leg." },
        ],
      },
      {
        key: "shift",
        title: "A same-port move",
        intro: "Anchor to dock, dock to dock, or anchor to anchor inside one port is a Shift, one event with one time.",
        steps: [
          { img: "shift-01.jpg", text: "Tap + Event and pick Shifted within port. Type the port, give the date and the time the ship started the move (letting go lines or weighing anchor), pick the line it goes after, and tap Add Shift. A shift has one time, not a start and an end, and no miles. Shifted more than once? Add one entry per shift." },
          { img: "shift-02.jpg", text: "The SHIFT line carries the port and its one time, on the OUT side." },
          { text: "If your slip shows the same port on two rows in a row with no shift between them, Preview and submit asks whether to add the Shift line before the preview opens. OK adds it; Cancel leaves the slip as it is. Either way you see the PDF next and can still go Back to editing. It asks only for ports, never for a headland." },
        ],
      },
      {
        key: "rate",
        title: "Hubbard, Snow Passage, Glacier Bay and the other rate events",
        intro: "These are billed as events, not by distance, so their miles stay blank on purpose. Never fill them in.",
        steps: [
          { img: "rate-01.jpg", text: "Snow Passage is on the slip as its own row with no miles. Leave its miles blank; the office table's Snow Passage distance is already in the next port's miles." },
          { img: "rate-02.jpg", text: "Hubbard Glacier is the same: a row with no miles, and a Hubbard round trip carries its miles on the Yakutat Pilot Station row. Glacier Bay, Tracy Arm, Endicott Arm and Misty Fjords are ordinary place rows: Update Miles fills the leg into them from the office table like any port." },
        ],
      },
      {
        key: "detention",
        title: "Detention and the other timed events",
        intro: "You record what happened and the times. The office works out any detention charge from them in its own system. Nothing on the slip asks you to decide what is detention.",
        steps: [
          { text: "These count, and each is an Event with a start and an end: Laying to / DP mode (underway but not making way), Stayed aboard in port at the master's request, Sailing delay (sailed an hour or more past the planned time, with the planned and actual times), Medical evacuation, and Anchored outside of a port such as Misty Fjords or William Henry Bay." },
          { text: "These are not detention: a shift inside a port, an anchoring inside a port, slow steaming, a wait for travel, and a pump run outside the headland. Detention outside pilotage waters needs no event at all; the office computes it from your transit times." },
          { img: "detention-01.jpg", text: "For example, the ship lays to off Juneau waiting for the berth. Tap + Event, pick Laying to / DP mode, say where, give the start and end date and time, pick the line it goes after (here Snow Passage, before the Juneau arrival), and tap Add Detention." },
          { img: "detention-02.jpg", text: "The event line shows the hours, worked out from your start and end. It carries no miles; the leg's miles stay on the arrival row." },
        ],
      },
      {
        key: "sign",
        title: "Sign once, Save Draft, and drafts that follow you",
        steps: [
          { img: "sign-01.jpg", text: "Sign in your block. Save as my signature keeps it, so the next slip, expense report or form can place it with one tap, with or without a signal." },
          { img: "save-01.jpg", text: "Save Draft saves the slip on the device and copies it to your account. A slip started on the iPad shows up on the laptop, and nothing is lost when the wifi drops." },
        ],
      },
      {
        key: "handoff",
        title: "Hand the slip to the other pilot by name",
        intro: "The pilot who finishes the trip files the slip. Send yours to them instead of retyping.",
        steps: [
          { img: "handoff-01.jpg", text: "Tap Second pilot handoff." },
          { img: "handoff-02.jpg", text: "Add a note if you like and tap Send to the other pilot's name, or pick Someone else. Nothing is on their slip yet: while they have the portal open with any connection, a green Handed to you card appears in their Trip Workspace within a minute or so, and they tap Add to Pilot Slip. That adds your block, segments and times to their slip. If you both typed a different time for the same field, they tap the right one on each line. Nothing else they typed changes, and everything stays editable." },
        ],
      },
      {
        key: "airdrop",
        title: "Hand off by AirDrop with no signal",
        steps: [
          { img: "airdrop-01.jpg", text: "With no signal at all, tap Share as file (AirDrop) under Second pilot handoff and AirDrop the file to the other pilot. They tap Import a slip file (AirDrop) and pick it. AirDrop help spells out the steps on each side." },
        ],
      },
      {
        key: "preview",
        title: "Preview PDF and Download Fillable PDF",
        steps: [
          { img: "preview-01.jpg", text: "Preview PDF shows the filled slip on screen before you send anything. Download Fillable PDF gives you the slip as a file to pass to a pilot who is not on the portal and files it the usual way." },
        ],
      },
      {
        key: "submit",
        title: "Preview and submit",
        intro: "With a connection, every slip is checked on its PDF before it goes, and nothing is sent until you tap Looks right, submit. With no signal, see Submit with no signal below.",
        steps: [
          { img: "submit-01.jpg", text: "When the trip is done and your block is filled in and signed, tap Preview and submit." },
          { text: "First the portal may ask up to three things. Miles that do not match the office table: OK fixes them, Cancel goes back to the slip. The same port on two rows in a row with no shift between them: OK adds the SHIFT line, Cancel leaves the slip as it is. Nobody marked as submitting the sheet: OK ticks your block, Cancel goes back to the slip." },
          { img: "submit-02.jpg", text: "The slip opens as the PDF the office will get. Nothing has been sent yet. Check it. Back to editing takes you back to fix something; Open in browser opens the same PDF in its own tab." },
          { img: "submit-03.jpg", text: "Tap Looks right, submit. If fields are still blank, the portal lists them and asks before it sends. Times that run out of order stop the slip until you fix them." },
          { text: "The message says it went to the office, to you and to every other pilot on the slip. The office gets the PDF and its data file. You and the other pilots get the PDF at the email address on the roster, with a subject like SEAPA Portal: Pilot Slip COR26-VOY05, Bosun and Anchor, Nov 9 to 12. A cargo or yacht slip goes the same way, with the PDF only. Nothing else to send. The slip moves to Filed." },
        ],
      },
      {
        key: "no-signal",
        title: "Submit with no signal",
        steps: [
          { text: "The preview is built on the server, so it needs a connection. With none, Preview and submit asks whether to submit without looking at the PDF. OK puts the slip in line on this device, and it sends itself when a connection returns. Cancel keeps the slip so you can preview it later." },
          { text: "If the connection drops while the slip is sending, the same thing happens: it waits on this device and sends itself. Either way there is nothing else to do. Until it goes, the line at the bottom of the account menu counts it as waiting to sync. Never clear the app's data while it waits." },
        ],
      },
      {
        key: "revise",
        title: "Correct a filed slip",
        steps: [
          { img: "submit-05.jpg", text: "In Filed, a slip you submitted has Correct this slip. Tap it and the slip reopens as you filed it, signatures included. A slip filed before the portal kept a reopenable copy says so instead." },
          { text: "Fix what was wrong and tap Preview and submit again. It files as a correction: the file names and the email subject end in REVISED v2 (v3 for the next one), and the office email asks them to discard the earlier version instead of importing both." },
          { text: "Only the pilot who submitted a slip can correct it. The other pilots on it see the filed copy, with a note that the pilot who submitted it files any correction." },
        ],
      },
      {
        key: "small-ship",
        title: "Small ship slips",
        steps: [
          { text: "A ship that is not on the CLAA calendar, such as a small expedition ship with its own itinerary, still starts from Trip Workspace: pick the vessel and the start date and tap Generate Trip. The portal builds the slip from the dispatch sheet and the office's route patterns instead of the CLAA calendar. Fill in and check the places the same way." },
        ],
      },
      {
        key: "cargo",
        title: "A cargo or yacht job",
        intro: "Cargo and yacht jobs are not on the dispatch sheet. Start one by hand.",
        steps: [
          { img: "cargo-01.jpg", text: "On My Schedule, tap Add a cargo or yacht job." },
          { img: "cargo-02.jpg", text: "Tap Start a Cargo Job or Start a Yacht Job. The links under it open the 2026 cargo and yacht pilotage sheets and the cargo instructions if you would rather fill one by hand and send it the usual way." },
          { img: "cargo-03.jpg", text: "Type the vessel name in capitals, or start typing and pick her if she has been here before, so what you entered last time comes back. Add the SEAPA job number from the Cargo & Yacht Dispatch, like 26-CAR-9, and the start date, then tap Start Cargo Job. You get the 2026 cargo or yacht form. The cargo form adds its own events: a dead ship movement, a continuous watch over 8 hours, standby." },
        ],
      },
    ],
  },
  {
    key: "pilot-after",
    title: "Pilot guide: after the trip, expenses and Filed",
    about: "The expense report, your filed paperwork, the Accounting tab, the Paperwork due card, and picking up where you left off.",
    topics: [
      {
        key: "expense",
        title: "Start the expense report from the trip",
        steps: [
          { img: "expense-01.jpg", text: "Open the trip and tap Expense Report. It is the office reimbursement form, filled on screen. Pick the vessel and your name." },
          { img: "expense-02.jpg", text: "Dispatch Start, On Ship, Off Ship and Dispatch End take a place, a date and a time. Sync From Pilot Slip copies them from your slip once it is filled in." },
          { img: "expense-03.jpg", text: "The dates run from your dispatch span, one column each. Your travel around this trip lists the flights and hotel from the reservations sheet and your own additions; costs you recorded on the Travel page fill into the grid when you sync. A flight with no recorded cost, such as a charter, is never claimed." },
          { img: "expense-04.jpg", text: "Per diem rates opens the rates that apply. The season follows each date on the report, not today: a line dated September 30 gets summer rates and a line dated October 1 gets winter rates, and the PDF uses the form for that season. A hotel night belongs to its check-in date." },
          { img: "expense-05.jpg", text: "Ships that pay per diem for meals and lodging are recognized by name, and the form says so above the receipts. Sign at the bottom; your saved signature works here too. Save Draft keeps the report on the device and in your account." },
        ],
      },
      {
        key: "receipts",
        title: "Receipts",
        steps: [
          { img: "receipts-01.jpg", text: "Tap Take photo or Choose file for each receipt. A photo becomes a one page PDF for you. With a connection it uploads right away; without one it is saved on this phone, marked uploads when online, and the report cannot be submitted until it has uploaded. Either way it is added to the submitted report as an extra page. A PDF you already scanned in Files, Dropbox or PDF Expert can be added as it is." },
        ],
      },
      {
        key: "expense-submit",
        title: "Preview and submit the expense report",
        steps: [
          { img: "submit-01.jpg", text: "Tap Preview and submit at the top of the report." },
          { img: "submit-02.jpg", text: "The report opens as the PDF the office and accounting will get, with each receipt as an extra page. Nothing has been sent yet. Back to editing takes you back; Looks right, submit sends it. A receipt attached offline has to upload before the preview can open." },
          { text: "Before it sends, the portal asks about an airline with no dollar amount (a charter needs none), a Royal Caribbean, Celebrity or Silversea ship with no receipts attached, and a PDF too large to attach, in which case the emails carry a download link good for 30 days." },
          { text: "The message says the PDF went to you and the report went to the office and to accounting. Nothing else to send. The email subject reads like SEAPA Portal: Expense Report COR26-VOY05, Bosun." },
          { text: "Submitting needs a connection. With none, nothing is sent and the message says so; the report stays as you left it, and Save Draft keeps it on this device. Submit again when you are back online." },
        ],
      },
      {
        key: "expense-revise",
        title: "Correct a filed expense report",
        steps: [
          { text: "In Filed, under Expense Reports, tap Correct this report. The report reopens with your figures. Sign it again: a correction is never filed with the earlier signature. Preview and submit files it as REVISED v2, and the office and accounting are asked to discard the earlier version. A report filed before the portal could reopen reports says so; start a new report for that voyage and it files as a revision." },
        ],
      },
      {
        key: "untied",
        title: "An expense report not tied to a trip",
        steps: [
          { img: "untied-01.jpg", text: "At the bottom of Trip Workspace, next to Need an expense report that is not tied to a trip?, tap Start one. It is the same form without a slip behind it." },
        ],
      },
      {
        key: "filed",
        title: "Filed: your slips and expense reports",
        steps: [
          { img: "filed-01.jpg", text: "Filed keeps everything submitted: your slips, the slips a co-pilot submitted for a trip you were dispatched on, and your expense reports. Anything still in progress stays in Trip Workspace." },
          { img: "filed-02.jpg", text: "Tap View PDF to open the filed copy. Nothing typed or drawn on it changes the filed slip; a correction is a revision filed by the pilot who submitted it. Filed copies open from your account and need a connection." },
          { img: "filed-03.jpg", text: "Expense Reports lists each submitted report with its total and View PDF." },
        ],
      },
      {
        key: "accounting",
        title: "The Accounting tab: your December numbers",
        steps: [
          { img: "accounting-01.jpg", text: "Accounting adds up the year from what the portal already knows: one row per submitted expense report with air out, air home, ground and lodging, and the per diem days counted from the dispatch sheet and the winter roster, with X days and FMLA taken out. Set the per diem rate once; it is remembered on this device. Add a trip from before the portal fills a gap, and Download CSV gives the table to your accountant." },
        ],
      },
      {
        key: "due",
        title: "Paperwork due",
        intro: "Pilot slips and expense reports are due within 24 hours of the trip ending, and an expense report is required even when there were no expenses.",
        steps: [
          { img: "due-01.jpg", text: "Once a trip is past that, the Paperwork due card on My Schedule lists what you still owe and how overdue it is. Submitting clears it." },
          { img: "due-02.jpg", text: "If you sent that slip or report to the office outside the portal, tap Sent outside the portal. That stops the reminders for it." },
          { text: "Reminder emails come from pilotslips@seapa.com: the first 48 hours after the trip ends, then about once a day. Only pilots who have filed through the portal before get them." },
        ],
      },
      {
        key: "resume",
        title: "Continue where you left off",
        steps: [
          { img: "resume-01.jpg", text: "My Schedule lists the paperwork you started but have not submitted, from any of your devices, with when it was last edited. Open the trip from its card below, or from Trip Workspace." },
          { img: "resume-02.jpg", text: "Trip Workspace shows the same under Continue where you left off, with Open. Archive puts a trip away: off this device, kept in your account under Archived trips. A trip archives itself once its slip and your expense report are both filed." },
        ],
      },
    ],
  },
  {
    key: "pilot-vessel",
    title: "Pilot guide: Vessel Info, questions and requests",
    about: "Past slips and the pilot card for any ship, the bridge team list, ship notes, Ask, Ask the Board, dispatch and time off requests, feedback and What's New.",
    topics: [
      {
        key: "vessel",
        title: "Vessel Info: past slips, the pilot card, the bridge team and notes",
        steps: [
          { img: "vessel-01.jpg", text: "Tap More, then Vessel Info, and pick a ship." },
          { img: "vessel-02.jpg", text: "Pilot Card holds a photo of the ship's pilot card. Photograph each page with the camera first, then tap Photograph card, choose Photo Library and pick all the pages together; they become one PDF. Or add a PDF you already have with Choose file. Pictures are not kept for offline use." },
          { img: "vessel-03.jpg", text: "Vessel Bridge Team is one shared list per ship: rank, name, watch, the date they disembark, a note. Every row shows who updated it and when; the last edit wins. Add crewmember adds a row." },
          { img: "vessel-04.jpg", text: "Ship Notes are working notes for the next pilot aboard, posted with your name and the date. Tick Just for me to keep a note private. You can edit or delete your own notes later." },
          { img: "vessel-05.jpg", text: "Pilot Slip History lists the slips submitted through the portal for that ship, so you can see how the last pilot wrote her up." },
        ],
      },
      {
        key: "ask",
        title: "Ask: the bylaws, rules, statutes, waterway guides and rates",
        steps: [
          { img: "ask-01.jpg", text: "Ask takes a plain question about the Bylaws, the Operating Rules, the Alaska marine pilot statutes and regulations, the voluntary waterway guides or the published rates. Type it and tap Ask. Every sentence of the answer cites the document and page it came from. It needs an internet connection. Your questions are private and nothing you ask is recorded. What documents does this search? lists them." },
        ],
      },
      {
        key: "leadership",
        title: "Ask the Board",
        intro: "A question or request for the SEAPA Board of Directors, for anything that is not urgent. For anything urgent or about safety, call dispatch.",
        steps: [
          { img: "leadership-01.jpg", text: "Tap More, then Ask the Board. Give it a subject and your question. Tick OK to share the answer with the whole group if the answer may be posted; leave it unticked for a private matter, and then only you and the BOD can see it. Tap Send to the Board." },
          { text: "Your question is emailed to the Board and kept in their portal queue. The answer appears on this page and reaches you by email." },
        ],
      },
      {
        key: "requests",
        title: "Requests: a ship, X days or FMLA",
        steps: [
          { img: "requests-01.jpg", text: "Tap More, then Requests. Dispatch Request asks for a ship or dates: pick the vessel or Any Vessel, the dates and a note, then Submit Request. It goes to the DispatchRequests group email, the same address as before, and into the board's request list." },
          { img: "requests-02.jpg", text: "Request X / FMLA Days asks for unpaid X days or FMLA, with the dates and a reason. What happens when you submit explains where it goes." },
          { img: "requests-03.jpg", text: "My Request History keeps every request with its status. YTD Totals counts the X days and FMLA days in this calendar year from the dates on each request, leaving out rejected ones." },
        ],
      },
      {
        key: "feedback",
        title: "Send feedback",
        steps: [
          { img: "feedback-01.jpg", text: "Tap More, then Send feedback at the bottom of the menu, or your initial at the top right, then Send feedback. It works from every screen, even one that failed to load." },
          { img: "feedback-02.jpg", text: "Say what happened and attach a screenshot or a video if you have one. The portal version and the page you were on come along on their own." },
        ],
      },
      {
        key: "whatsnew",
        title: "What's New",
        steps: [
          { img: "whatsnew-01.jpg", text: "Tap More, then What's New, for every portal update and note from the developer, newest first. A note you have not read puts a red dot on More." },
        ],
      },
    ],
  },
  {
    key: "pilot-license",
    title: "Pilot guide: your profile, Continuing Education and sea service",
    about: "Your profile and endorsement requests, logging CE credits and the renewal report, and the sea service letter.",
    topics: [
      {
        key: "profile",
        title: "My Profile",
        steps: [
          { img: "profile-01.jpg", text: "Tap your initial at the top right, then My Profile. Your full name is printed on your slips and expense reports exactly as typed here. Keep your dispatch port, state license number, license size and Mariner Reference Number current; the MRN is needed for the sea service letter. Tap Save Changes." },
          { img: "profile-02.jpg", text: "Request an Endorsement sends a request to the Training Chair by email. The cargo request includes your portal-tracked cargo work. The Training Pilot request asks for the letter to the State Marine Pilot Coordinator. Your profile shows the request as on file, requested, or declined with the reason." },
        ],
      },
      {
        key: "ce",
        title: "Continuing Education",
        intro: "Three CE credits each renewal, at least one from a Board-approved simulator or manned model course. A manned model course counts for two. A regional waterway observer trip is one credit, and observer trips count for at most one credit per renewal. Courses from the three biennial periods before your renewal count.",
        steps: [
          { img: "ce-01.jpg", text: "Tap More, then Continuing Education. The page shows what you need to renew and your standing against it. Keep your own certificates; the State may ask for one up to six years after the course." },
          { img: "ce-02.jpg", text: "Under Submit Course Credit, tap Start Form." },
          { img: "ce-03.jpg", text: "Give the date completed, pick the approved course, and add the exact name from the certificate if it differs. Attach a copy of the certificate with Take photo or Choose file if you like; it is for the Training Chair's convenience and does not replace your own copy. Tap Submit for Credit. Observer Trip Report has its own form with the vessel, dates and captain." },
          { img: "ce-04.jpg", text: "Generate License Renewal CE Report builds the report for your renewal from what you have logged." },
        ],
      },
      {
        key: "sea-service",
        title: "The sea service letter",
        intro: "Your service days for the USCG renewal, counted over the past five years from the office's sea service sheets and the live dispatch sheet.",
        steps: [
          { img: "sea-01.jpg", text: "Tap More, then Sea Service. The letter period runs five years back from today; change the dates if you need another window. The service record lists each ship with its IMO number, tonnage and days, with the total against the 360 the renewal needs. Your MRN has to be on your profile first." },
          { img: "sea-02.jpg", text: "A cargo or yacht slip filed through the portal writes its days here on its own, for every pilot on the slip. Add only a job that was not filed through the portal, under Add cargo or other vessel time, with the dates and the vessel, then tap Add days. Days you worked as a trainee or deputy count the same." },
          { img: "sea-03.jpg", text: "Send to Training Chair sends the table for signature. After the Training Chair signs it in the portal, the final PDF is emailed to you for your renewal package. The totals are computed from the rows, never hand-summed." },
        ],
      },
    ],
  },
  {
    key: "pilot-phone",
    title: "Pilot guide: the portal on your phone",
    about: "Installing it like an app, what works with no signal, updates, the account menu and dark mode.",
    topics: [
      {
        key: "install",
        title: "Install it like an app",
        steps: [
          { img: "phone-01.jpg", text: "Open the portal in Safari, tap Share, then Add to Home Screen. It opens like an app, and the whole portal, the blank forms and the fonts are kept on the phone so it opens with no signal. On an Android phone, use the browser's Add to Home screen or Install app." },
        ],
      },
      {
        key: "offline",
        title: "No signal",
        intro: "The portal does not trust the phone's own online light, because ship and PPU wifi can say online with no internet behind it. It checks with a real round trip before it acts.",
        steps: [
          { img: "offline-01.jpg", text: "With no internet, a plain banner says the portal is showing the last-loaded data, and that changes may not save until you are back online. Anything you opened in the last week, such as your schedule, a brief or Filed, still shows. Drafts save on the phone. When you have a connection again, open the slip and wait for the synced status before you count on the copy in your account." },
          { text: "When an update is ready, a banner offers Reload. It appears once and installs only when the internet is really reachable, so it never leaves you with half an app at sea." },
        ],
      },
      {
        key: "account",
        title: "The account menu and what is only on this phone",
        steps: [
          { img: "account-01.jpg", text: "Tap your initial at the top right." },
          { img: "account-02.jpg", text: "The menu holds My Profile, Change password, Terms & Privacy, Portal Support, Send feedback and Logout. Change password opens a page where you type a new password twice, at least six characters, and tap Change password; use the new one next time you sign in. The line under them counts submits waiting to send, receipts waiting to upload and unsaved slip edits held only on this phone. It does not count an expense report you worked on offline, which also stays only on this phone until it saves to your account. Never clear the app's data while you have work that has not synced; it would be the only copy." },
        ],
      },
      {
        key: "dark",
        title: "Dark mode and the red dots",
        steps: [
          { img: "dark-01.jpg", text: "The moon at the top turns on dark mode for night work; the sun turns it off." },
          { img: "dark-02.jpg", text: "A red dot on Dispatch means a dispatch or travel change you have not looked at, or paperwork that is overdue. Opening Dispatch or Travel clears the change dot; the paperwork dot clears when the slip or report is filed. A red dot on More means something new inside the menu, such as a note on What's New, and clears when you open that item." },
          { img: "more-01.jpg", text: "More holds everything else: Pilot tools, Training, and Portal with What's New and Help." },
        ],
      },
    ],
  },
];
